import { Injectable } from '@angular/core';
import { Agency, Announcement, Product, ServiceItem } from '../models/public.models';

@Injectable({ providedIn: 'root' })
export class PublicDataService {

  getAnnouncements(): Announcement[] {
    return [
      {
        id: 'hero-1',
        title: 'Protégez votre avenir avec confiance',
        subtitle: 'Des solutions d\'assurance adaptées à vos besoins au Cameroun et en Afrique centrale.',
        imageUrl: 'assets/banners/hero-family.jpg',
        ctaLabel: 'CONTACT US',
        ctaRoute: '/nous-contacter',
      },
      {
        id: 'hero-2',
        title: 'Assurance auto dès 12 500 FCFA/mois',
        subtitle: 'Roulez en toute sérénité avec une couverture complète pour votre véhicule.',
        imageUrl: 'assets/banners/hero-car.jpg',
        ctaLabel: 'VOIR NOS OFFRES',
        ctaRoute: '/produits/auto',
      },
      {
        id: 'hero-3',
        title: 'Votre santé, notre priorité',
        subtitle: 'Accédez aux meilleurs soins avec nos formules santé flexibles.',
        imageUrl: 'assets/banners/hero-health.jpg',
        ctaLabel: 'DÉCOUVRIR',
        ctaRoute: '/produits/sante',
      },
    ];
  }

  getProducts(): Product[] {
    return [
      {
        id: 'auto',
        name: 'Assurance Auto',
        imageUrl: 'assets/products/product-auto.jpg',
        rating: '4.8',
        descriptions: [
          { title: 'Responsabilité civile', body: 'Couvre les dommages causés aux tiers lors d\'un accident.' },
          { title: 'Tous risques', body: 'Protection complète incluant vol, incendie et bris de glace.' },
        ],
        packages: [
          {
            name: 'Essentiel',
            pricePerMonthCfa: 12500,
            features: ['Responsabilité civile', 'Assistance dépannage 24h'],
            formulas: [
              { title: 'Tiers simple', description: 'Couverture minimale obligatoire.' },
              { title: 'Tiers étendu', description: 'Inclut vol et incendie.' },
              { title: 'Tiers complet', description: 'Ajoute bris de glace et catastrophes naturelles.' },
            ],
          },
          {
            name: 'Confort',
            pricePerMonthCfa: 15000,
            highlight: true,
            features: ['Responsabilité civile', 'Assistance 24h', 'Véhicule de remplacement'],
            formulas: [
              { title: 'Intermédiaire', description: 'RC + vol + incendie + bris de glace.' },
              { title: 'Intermédiaire+', description: 'Ajoute dommages collision.' },
              { title: 'Tous risques light', description: 'Tous risques avec franchise modérée.' },
            ],
          },
          {
            name: 'Premium',
            pricePerMonthCfa: 22000,
            features: ['Tous risques', 'Assistance premium', 'Véhicule de remplacement', '0 franchise'],
            formulas: [
              { title: 'Tous risques', description: 'Couverture intégrale sans franchise.' },
              { title: 'Tous risques+', description: 'Inclut valeur à neuf 2 ans.' },
              { title: 'VIP', description: 'Tous risques + chauffeur de remplacement.' },
              { title: 'Flotte', description: 'Solution pour 3+ véhicules.' },
            ],
          },
        ],
      },
      {
        id: 'sante',
        name: 'Assurance Santé',
        imageUrl: 'assets/products/product-sante.jpg',
        rating: '4.9',
        descriptions: [
          { title: 'Hospitalisation', body: 'Prise en charge des frais d\'hospitalisation.' },
          { title: 'Soins courants', body: 'Consultations, pharmacie, analyses et imagerie.' },
        ],
        packages: [
          {
            name: 'Individuel',
            pricePerMonthCfa: 18000,
            features: ['Consultations', 'Pharmacie', 'Analyses'],
            formulas: [
              { title: 'Basique', description: 'Soins courants uniquement.' },
              { title: 'Confort', description: 'Soins + hospitalisation.' },
              { title: 'Premium', description: 'Soins + hospitalisation + dentaire + optique.' },
            ],
          },
          {
            name: 'Famille',
            pricePerMonthCfa: 22000,
            highlight: true,
            features: ['Couverture familiale', 'Maternité', 'Pédiatrie', 'Hospitalisation'],
            formulas: [
              { title: 'Famille Essentiel', description: 'Couple + 2 enfants, soins courants.' },
              { title: 'Famille Confort', description: 'Couple + 4 enfants, soins + hospitalisation.' },
              { title: 'Famille Premium', description: 'Illimité enfants, couverture complète.' },
              { title: 'Famille VIP', description: 'Premium + évacuation sanitaire.' },
            ],
          },
          {
            name: 'Entreprise',
            pricePerMonthCfa: 35000,
            features: ['Couverture employés', 'Médecine du travail', 'Check-up annuel'],
            formulas: [
              { title: 'PME', description: '5-20 employés.' },
              { title: 'Corporate', description: '20-100 employés.' },
              { title: 'Grand compte', description: '100+ employés, sur mesure.' },
            ],
          },
        ],
      },
      {
        id: 'habitation',
        name: 'Assurance Habitation',
        imageUrl: 'assets/products/product-habitation.jpg',
        rating: '4.7',
        descriptions: [
          { title: 'Multirisque habitation', body: 'Protection contre incendie, dégât des eaux, vol.' },
          { title: 'Responsabilité civile', body: 'Couvre les dommages causés à autrui depuis votre domicile.' },
        ],
        packages: [
          {
            name: 'Locataire',
            pricePerMonthCfa: 8000,
            features: ['RC occupant', 'Vol', 'Dégât des eaux'],
            formulas: [
              { title: 'Studio', description: 'Surface < 30m².' },
              { title: 'Appartement', description: 'Surface 30-80m².' },
              { title: 'Grand logement', description: 'Surface > 80m².' },
            ],
          },
          {
            name: 'Propriétaire',
            pricePerMonthCfa: 12000,
            highlight: true,
            features: ['Multirisque', 'RC propriétaire', 'Catastrophes naturelles'],
            formulas: [
              { title: 'Essentiel', description: 'Couverture de base propriétaire.' },
              { title: 'Confort', description: 'Ajoute piscine, jardin, dépendances.' },
              { title: 'Premium', description: 'Valeur à neuf, relogement temporaire.' },
              { title: 'Villa', description: 'Sur mesure pour grandes propriétés.' },
            ],
          },
          {
            name: 'Immeuble',
            pricePerMonthCfa: 25000,
            features: ['Parties communes', 'RC copropriété', 'Protection juridique'],
            formulas: [
              { title: 'Petit immeuble', description: '< 10 lots.' },
              { title: 'Résidence', description: '10-50 lots.' },
              { title: 'Grand ensemble', description: '50+ lots.' },
            ],
          },
        ],
      },
    ];
  }

  getServices(): ServiceItem[] {
    return [
      {
        icon: 'shopping_cart',
        title: 'Souscrire à une police',
        description: '',
        route: '/se-connecter',
      },
      {
        icon: 'credit_card',
        title: "Payer ma prime d'assurance",
        description: '',
        route: '/se-connecter',
      },
      {
        icon: 'track_changes',
        title: 'Déclarer un sinistre',
        description: '',
        route: '/nous-contacter',
      },
      {
        icon: 'help_outline',
        title: "Besoin d'aide",
        description: '',
        route: '/nous-contacter',
      },
      {
        icon: 'apartment',
        title: 'Voir nos agences',
        description: '',
        route: '/accueil',
        fragment: 'agences',
      },
      {
        icon: 'autorenew',
        title: 'Faire une simulation',
        description: '',
        route: '/se-connecter',
      },
    ];
  }

  getAgencies(): Agency[] {
    return [
      {
        id: 'dla-akwa',
        name: 'Agence Douala Akwa',
        city: 'Douala',
        address: 'Rue Joss, Akwa, Douala',
        phone: '+237 233 42 56 78',
        email: 'akwa@saarvieprotection.com',
        lat: 4.0483,
        lng: 9.7043,
      },
      {
        id: 'dla-bonaberi',
        name: 'Agence Douala Bonabéri',
        city: 'Douala',
        address: 'Carrefour Bonabéri, Douala',
        phone: '+237 233 47 89 01',
        email: 'bonaberi@saarvieprotection.com',
        lat: 4.0725,
        lng: 9.6753,
      },
      {
        id: 'yde-centre',
        name: 'Agence Yaoundé Centre',
        city: 'Yaoundé',
        address: 'Avenue Kennedy, Centre Administratif, Yaoundé',
        phone: '+237 222 23 45 67',
        email: 'yaounde@saarvieprotection.com',
        lat: 3.8480,
        lng: 11.5021,
      },
      {
        id: 'yde-bastos',
        name: 'Agence Yaoundé Bastos',
        city: 'Yaoundé',
        address: 'Quartier Bastos, Yaoundé',
        phone: '+237 222 20 11 22',
        email: 'bastos@saarvieprotection.com',
        lat: 3.8764,
        lng: 11.5085,
      },
      {
        id: 'bafoussam',
        name: 'Agence Bafoussam',
        city: 'Bafoussam',
        address: 'Carrefour Total, Bafoussam',
        phone: '+237 233 44 55 66',
        email: 'bafoussam@saarvieprotection.com',
        lat: 5.4764,
        lng: 10.4175,
      },
    ];
  }
}
